import {
  useState,
  useCallback,
  useRef,
  useMemo,
  type CSSProperties,
  type ChangeEvent,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
  type SyntheticEvent,
} from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import WallpaperIcon from '@mui/icons-material/Wallpaper'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  setBgMode,
  setBgAngle,
  setBgImageUrl,
  setBgImageKey,
  setBgImageFit,
  setBgImageOffset,
  setBgImageZoom,
  selectActiveScene,
} from '../../../common/components/store/scenesSlice'
import type {BgMode, BgImageFit} from '../../../common/components/store/scenesSlice'
import AnglePicker from '../sharedWidgets/AnglePicker'
import OffsetPad from '../sharedWidgets/OffsetPad'
import GradientEditor from './GradientEditor'
import BgPresetModal from './BgPresetModal'
import {sealUndo} from '../../../common/undoable'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Helpers ───────────────────────────────────────────────────────

const loadImageFile = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })

// ── SceneSettingsPanel — background sub-accordion ─────────────────

const SceneSettingsPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const bgMode = useAppSelector((s) => selectActiveScene(s).bgMode)
  const bgAngle = useAppSelector((s) => selectActiveScene(s).bgAngle)
  const bgImageKey = useAppSelector((s) => selectActiveScene(s).bgImageKey)
  const bgImageUrl = useAppSelector((s) => selectActiveScene(s).bgImageUrl)
  const bgImageFit = useAppSelector((s) => selectActiveScene(s).bgImageFit)
  const bgImageOffsetX = useAppSelector((s) => selectActiveScene(s).bgImageOffsetX)
  const bgImageOffsetY = useAppSelector((s) => selectActiveScene(s).bgImageOffsetY)
  const bgImageZoom = useAppSelector((s) => selectActiveScene(s).bgImageZoom)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [bgModalOpen, setBgModalOpen] = useState(false)

  // ── Tab switch ──────────────────────────────────────────────────

  const handleTabChange = useCallback(
    (_: SyntheticEvent, newValue: number) => {
      const mode: BgMode = newValue === 0 ? 'gradient' : 'image'
      dispatch(setBgMode(mode))
    },
    [dispatch],
  )

  // ── Gradient handlers ───────────────────────────────────────────

  const handleAngleChange = useCallback(
    (angle: number) => {
      dispatch(setBgAngle(angle))
    },
    [dispatch],
  )

  const handleSealUndo = useCallback(() => {
    dispatch(sealUndo())
  }, [dispatch])

  // ── Image upload via button ─────────────────────────────────────

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const dataUrl = await loadImageFile(file)
      dispatch(setBgImageUrl(dataUrl))
      trackEvent('asset_uploaded', {kind: 'bg', format: file.type, size_bytes: file.size, editor_mode: 'paid'})
      e.target.value = ''
    },
    [dispatch],
  )

  // ── Drag and drop on image preview ──────────────────────────────

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        trackEvent('asset_upload_rejected', {kind: 'bg', reason: 'bad_format', editor_mode: 'paid'})
        return
      }
      const dataUrl = await loadImageFile(file)
      dispatch(setBgImageUrl(dataUrl))
      trackEvent('asset_uploaded', {kind: 'bg', format: file.type, size_bytes: file.size, editor_mode: 'paid'})
    },
    [dispatch],
  )

  // ── Image fit mode ──────────────────────────────────────────────

  const handleFitChange = useCallback(
    (_: ReactMouseEvent<HTMLElement>, value: BgImageFit | null) => {
      if (value) dispatch(setBgImageFit(value))
    },
    [dispatch],
  )

  // ── Offset pad ─────────────────────────────────────────────────

  const handleOffsetChange = useCallback(
    (x: number, y: number) => {
      dispatch(setBgImageOffset({x, y}))
    },
    [dispatch],
  )

  const handleZoomChange = useCallback(
    (_: Event, value: number | number[]) => {
      dispatch(setBgImageZoom((value as number) / 100))
    },
    [dispatch],
  )

  // ── Background preset modal ────────────────────────────────────

  const handleBgModalOpen = useCallback(() => {
    setBgModalOpen(true)
  }, [])

  const handleBgModalClose = useCallback(() => {
    setBgModalOpen(false)
  }, [])

  const handleBgPresetSelect = useCallback(
    (key: string, url: string) => {
      dispatch(setBgImageKey(key))
      dispatch(setBgImageUrl(url))
      dispatch(sealUndo())
    },
    [dispatch],
  )

  const tabIndex = bgMode === 'gradient' ? 0 : 1
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Accordion disableGutters square sx={sx.subAccordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.subSummary}>
        <WallpaperIcon sx={sx.subIcon} />
        <Typography sx={sx.subTitle}>{t('scene.background.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={sx.subDetails}>
        <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth" sx={sx.tabs}>
          <Tab label={t('scene.background.gradient')} sx={sx.tab} />
          <Tab label={t('scene.background.image')} sx={sx.tab} />
        </Tabs>

        {bgMode === 'gradient' && (
          <Box sx={sx.gradientContent}>
            {/* Multi-stop gradient editor */}
            <GradientEditor />

            {/* Angle picker */}
            <Box sx={sx.angleSection}>
              <Typography sx={sx.colorLabel}>{t('scene.background.angle')}</Typography>
              <Box sx={sx.angleRow}>
                <AnglePicker value={bgAngle} onChange={handleAngleChange} onDragEnd={handleSealUndo} />
                <Typography sx={sx.angleValue}>{bgAngle}°</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {bgMode === 'image' && (
          <Box sx={sx.imageContent}>
            {/* Drop zone + preview */}
            <Box
              sx={{...sx.dropZone, ...(dragOver ? sx.dropZoneActive : {})}}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {bgImageUrl ? (
                <Box component="img" src={bgImageUrl} alt={t('a11y.backgroundPreview')} sx={sx.imagePreview} />
              ) : (
                <Box sx={sx.emptyPreview}>
                  <UploadFileIcon sx={{fontSize: 24, opacity: 0.4}} />
                  <Typography sx={sx.dropText}>{t('scene.background.dropHere')}</Typography>
                </Box>
              )}
              {dragOver && (
                <Box sx={sx.dropOverlay}>
                  <Typography sx={sx.dropText}>{t('scene.background.dropHere')}</Typography>
                </Box>
              )}
            </Box>

            {/* Upload button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={handleUploadClick}
              sx={sx.uploadButton}
            >
              {t('scene.background.upload')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{display: 'none'}}
            />

            {/* Browse presets button */}
            <Button variant="outlined" size="small" onClick={handleBgModalOpen} sx={sx.uploadButton}>
              {t('scene.background.change')}
            </Button>

            {/* Fit mode toggle */}
            <Box sx={sx.settingsSection}>
              <Typography sx={sx.colorLabel}>{t('scene.background.fitMode')}</Typography>
              <ToggleButtonGroup
                value={bgImageFit}
                exclusive
                onChange={handleFitChange}
                size="small"
                fullWidth
                sx={sx.fitToggle}
              >
                <ToggleButton value="fill" sx={sx.fitButton}>
                  {t('scene.background.fill')}
                </ToggleButton>
                <ToggleButton value="contain" sx={sx.fitButton}>
                  {t('scene.background.contain')}
                </ToggleButton>
                <ToggleButton value="cover" sx={sx.fitButton}>
                  {t('scene.background.cover')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Offset pad + Zoom slider */}
            <Box sx={sx.settingsSection}>
              <Typography sx={sx.colorLabel}>{t('scene.background.offset')}</Typography>
              <Box sx={sx.offsetZoomRow}>
                <OffsetPad
                  valueX={bgImageOffsetX}
                  valueY={bgImageOffsetY}
                  min={-100}
                  max={100}
                  onChangeX={() => {}}
                  onChangeY={() => {}}
                  onChange={handleOffsetChange}
                  onDragEnd={handleSealUndo}
                />
                <Box sx={sx.zoomSliderBox}>
                  <Typography sx={sx.zoomLabel}>{t('scene.background.zoom')}</Typography>
                  <Slider
                    value={Math.round(bgImageZoom * 100)}
                    min={100}
                    max={500}
                    step={1}
                    orientation="vertical"
                    onChange={handleZoomChange}
                    onChangeCommitted={handleSealUndo}
                    sx={sx.zoomSlider}
                    size="small"
                  />
                  <Typography sx={sx.zoomValue}>{Math.round(bgImageZoom * 100)}%</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        <BgPresetModal
          open={bgModalOpen}
          onClose={handleBgModalClose}
          onSelect={handleBgPresetSelect}
          currentKey={bgImageKey}
        />
      </AccordionDetails>
    </Accordion>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  subAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  subSummary: {
    minHeight: 40,
    padding: '0 4px',
    '&.Mui-expanded': {minHeight: 40},
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      alignItems: 'center',
      gap: '6px',
      '&.Mui-expanded': {margin: '8px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 18,
  },
  subIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 16,
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.secondary,
  },
  subDetails: {
    padding: '4px 4px 12px',
  },
  tabs: {
    minHeight: 32,
    marginBottom: '12px',
    '& .MuiTabs-indicator': {
      height: 2,
    },
  },
  tab: {
    minHeight: 32,
    fontSize: 11,
    fontWeight: 500,
    textTransform: 'none' as const,
    color: tokens.text.muted,
    '&.Mui-selected': {
      color: tokens.text.primary,
    },
  },
  gradientContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  colorRow: {
    display: 'flex',
    gap: '12px',
  },
  colorField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  colorLabel: {
    fontSize: 11,
    color: tokens.text.muted,
    fontWeight: 500,
  },
  colorInput: {
    width: '100%',
    height: 32,
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: 6,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 2,
  } as CSSProperties,
  angleSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  },
  angleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  angleValue: {
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text.primary,
    minWidth: 40,
  },
  imageContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    alignItems: 'stretch',
  },
  dropZone: {
    position: 'relative' as const,
    borderRadius: '6px',
    border: `2px dashed ${tokens.accordion.border}`,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
    cursor: 'pointer',
  },
  dropZoneActive: {
    borderColor: tokens.text.primary,
  },
  imagePreview: {
    width: '100%',
    height: 120,
    objectFit: 'cover',
    display: 'block',
  },
  emptyPreview: {
    width: '100%',
    height: 120,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    color: tokens.text.muted,
  },
  dropOverlay: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(2px)',
  },
  dropText: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  uploadButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    color: tokens.text.secondary,
    borderColor: tokens.accordion.border,
    '&:hover': {
      borderColor: tokens.text.muted,
    },
  },
  settingsSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  fitToggle: {
    '& .MuiToggleButtonGroup-grouped': {
      border: `1px solid ${tokens.accordion.border}`,
      '&.Mui-selected': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: tokens.text.primary,
      },
    },
  },
  fitButton: {
    fontSize: 10,
    fontWeight: 500,
    textTransform: 'none' as const,
    color: tokens.text.muted,
    padding: '4px 8px',
  },
  offsetZoomRow: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '8px',
  },
  zoomSliderBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '2px',
    minWidth: 24,
  },
  zoomLabel: {
    fontSize: 9,
    fontWeight: 600,
    color: tokens.text.muted,
  },
  zoomSlider: {
    flex: 1,
    '& .MuiSlider-thumb': {width: 10, height: 10},
    '& .MuiSlider-rail': {opacity: 0.3},
  },
  zoomValue: {
    fontSize: 9,
    fontWeight: 500,
    color: tokens.text.secondary,
  },
})

export default SceneSettingsPanel
